import React from 'react';
import { Progress as AntProgress, ProgressProps as AntProgressProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface ProgressProps extends AntProgressProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  progressBase: {
    '& .ant-progress-text': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.medium,
    },
  },
  progressLine: {
    '& .ant-progress-bg': {
      backgroundColor: theme.color.semantic.brand.default.primary,
    },
    '& .ant-progress-inner': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
    },
    '& .ant-progress-text': {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
    },
  },
  progressLineActive: {
    '& .ant-progress-bg': {
      backgroundColor: theme.color.semantic.brand.default.primary,
    },
  },
  progressLineSuccess: {
    '& .ant-progress-bg': {
      backgroundColor: theme.color.semantic.accent.green.strong,
    },
    '& .ant-progress-text': {
      color: theme.color.semantic.accent.green.strong,
    },
  },
  progressLineException: {
    '& .ant-progress-bg': {
      backgroundColor: theme.color.semantic.brand.danger.primary,
    },
    '& .ant-progress-text': {
      color: theme.color.semantic.brand.danger.primary,
    },
  },
  progressCircle: {
    '& .ant-progress-text': {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
    },
    '& .ant-progress-circle-trail': {
      stroke: theme.color.semantic.background.neutral.subtle,
    },
  },
  progressCircleActive: {
    '& .ant-progress-circle-path': {
      stroke: theme.color.semantic.brand.default.primary,
    },
  },
  progressCircleSuccess: {
    '& .ant-progress-circle-path': {
      stroke: theme.color.semantic.accent.green.strong,
    },
    '& .ant-progress-text': {
      color: theme.color.semantic.accent.green.strong,
    },
  },
  progressCircleException: {
    '& .ant-progress-circle-path': {
      stroke: theme.color.semantic.brand.danger.primary,
    },
    '& .ant-progress-text': {
      color: theme.color.semantic.brand.danger.primary,
    },
  },
  progressDashboard: {
    '& .ant-progress-text': {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
    },
    '& .ant-progress-circle-trail': {
      stroke: theme.color.semantic.background.neutral.subtle,
    },
  },
  progressDashboardActive: {
    '& .ant-progress-circle-path': {
      stroke: theme.color.semantic.brand.default.primary,
    },
  },
  progressDashboardSuccess: {
    '& .ant-progress-circle-path': {
      stroke: theme.color.semantic.accent.green.strong,
    },
    '& .ant-progress-text': {
      color: theme.color.semantic.accent.green.strong,
    },
  },
  progressDashboardException: {
    '& .ant-progress-circle-path': {
      stroke: theme.color.semantic.brand.danger.primary,
    },
    '& .ant-progress-text': {
      color: theme.color.semantic.brand.danger.primary,
    },
  },
  progressSteps: {
    '& .ant-progress-steps-item': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
    },
    '& .ant-progress-steps-item-active': {
      backgroundColor: theme.color.semantic.brand.default.primary,
    },
  },
  progressStepsSuccess: {
    '& .ant-progress-steps-item-active': {
      backgroundColor: theme.color.semantic.accent.green.strong,
    },
  },
  progressStepsException: {
    '& .ant-progress-steps-item-active': {
      backgroundColor: theme.color.semantic.brand.danger.primary,
    },
  },
  progressSmall: {
    '& .ant-progress-text': {
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.regular,
    },
  },
  progressDefault: {
    '& .ant-progress-text': {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
    },
  },
}));

const Progress: React.FC<ProgressProps> = ({
  type = 'line',
  status,
  size = 'default',
  className = '',
  strokeLinecap = 'round',
  ...rest
}) => {
  const classes = useStyles();

  const getProgressClass = () => {
    const baseClass = classes.progressBase;
    let typeClass = '';
    let statusClass = '';
    let sizeClass = '';

    // Type classes
    if (type === 'line') {
      typeClass = classes.progressLine;
      if (status === 'success') statusClass = classes.progressLineSuccess;
      else if (status === 'exception') statusClass = classes.progressLineException;
      else if (status === 'active') statusClass = classes.progressLineActive;
    } else if (type === 'circle') {
      typeClass = classes.progressCircle;
      if (status === 'success') statusClass = classes.progressCircleSuccess;
      else if (status === 'exception') statusClass = classes.progressCircleException;
      else if (status === 'active') statusClass = classes.progressCircleActive;
    } else if (type === 'dashboard') {
      typeClass = classes.progressDashboard;
      if (status === 'success') statusClass = classes.progressDashboardSuccess;
      else if (status === 'exception') statusClass = classes.progressDashboardException;
      else if (status === 'active') statusClass = classes.progressDashboardActive;
    }

    // Steps type
    if (rest.steps) {
      typeClass = classes.progressSteps;
      if (status === 'success') statusClass = classes.progressStepsSuccess;
      else if (status === 'exception') statusClass = classes.progressStepsException;
    }

    // Size classes
    if (size === 'small') {
      sizeClass = classes.progressSmall;
    } else {
      sizeClass = classes.progressDefault;
    }

    return `${baseClass} ${typeClass} ${statusClass} ${sizeClass}`.trim();
  };

  const combinedClassName = `${getProgressClass()} ${className}`.trim();

  return (
    <AntProgress
      type={type}
      status={status}
      size={size}
      strokeLinecap={strokeLinecap}
      {...rest}
      className={combinedClassName}
    />
  );
};

export default Progress;
